import { SITE_URL } from "./locale";
import { pages, OG_IMAGE_URL, OG_IMAGE_ALT, OG_IMAGE_TYPE, OG_IMAGE_WIDTH, OG_IMAGE_HEIGHT, type PageKey } from "./seo-pages";

export function serviceSchema(opts: {
  name: string;
  description: string;
  url: string;
  serviceType?: string;
  areaServed?: string[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: opts.name,
    description: opts.description,
    serviceType: opts.serviceType ?? opts.name,
    areaServed: opts.areaServed ?? ["DE", "AT", "CH"],
    provider: {
      "@type": "Organization",
      name: "Calvarius GmbH",
      url: SITE_URL,
    },
    url: `${SITE_URL}${opts.url}`,
  };
}

export function breadcrumbSchema(items: { name: string; path: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE_URL}${it.path}`,
    })),
  };
}

export function jsonLdScript(data: unknown) {
  return {
    type: "application/ld+json" as const,
    children: JSON.stringify(data),
  };
}

type MetaTag = Record<string, string>;
type LinkTag = Record<string, string>;

/**
 * Build per-page <head> meta and links from the central seo-pages config.
 * Returns { meta, links } that can be spread or merged into a route's head().
 */
export function pageHead(key: PageKey, locale: "de" | "en"): { meta: MetaTag[]; links: LinkTag[] } {
  const page = pages[key] as (typeof pages)[PageKey] & { noindex?: boolean };
  const cur = page[locale];
  const canonical = `${SITE_URL}${cur.path}`;
  const ogLocale = locale === "de" ? "de_DE" : "en_US";
  const ogLocaleAlt = locale === "de" ? "en_US" : "de_DE";

  const meta: MetaTag[] = [
    { title: cur.title },
    { name: "description", content: cur.description },
    { property: "og:title", content: cur.title },
    { property: "og:description", content: cur.description },
    { property: "og:image", content: OG_IMAGE_URL },
    { property: "og:image:secure_url", content: OG_IMAGE_URL },
    { property: "og:image:type", content: OG_IMAGE_TYPE },
    { property: "og:image:width", content: OG_IMAGE_WIDTH },
    { property: "og:image:height", content: OG_IMAGE_HEIGHT },
    { property: "og:image:alt", content: OG_IMAGE_ALT },
    { property: "og:type", content: "website" },
    { property: "og:url", content: canonical },
    { property: "og:locale", content: ogLocale },
    { property: "og:locale:alternate", content: ogLocaleAlt },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: cur.title },
    { name: "twitter:description", content: cur.description },
    { name: "twitter:image", content: OG_IMAGE_URL },
    { name: "twitter:image:alt", content: OG_IMAGE_ALT },
  ];

  if (page.noindex) {
    meta.push({ name: "robots", content: "noindex, follow" });
  }

  const links: LinkTag[] = [
    { rel: "canonical", href: canonical },
    { rel: "alternate", hrefLang: "de", href: `${SITE_URL}${page.de.path}` },
    { rel: "alternate", hrefLang: "en", href: `${SITE_URL}${page.en.path}` },
    { rel: "alternate", hrefLang: "x-default", href: `${SITE_URL}${page.de.path}` },
  ];

  return { meta, links };
}
